/* =====================================================================
 * PdfRenderer — PDF 파일 캔버스 렌더링 (pdfjs-dist)
 * ===================================================================== */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled from 'styled-components';
import * as pdfjsLib from 'pdfjs-dist';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import { fitPageScale, useViewportSize, type PageSize } from '@auw/workspace-utils';
import { getTenantSnapshot } from '@auw/onprem';
import {
  resolveTenantPdfWorkerUrl,
  resolveTenantPdfCMapUrl,
  resolveTenantPdfStandardFontDataUrl,
  resolveTenantPdfWasmUrl,
} from '@auw/tenant';

/* ------------------------------------------------------------------ */
/* Worker + runtime asset resolution                                   */
/* ------------------------------------------------------------------ */
/* B2C pulls `unpkg.com/pdfjs-dist@<version>/…` — all three URLs live
 * in the B2C tenant manifest (`assets.pdfWorkerUrl`, `pdfCMapUrl`,
 * `pdfStandardFontDataUrl`) with `{version}` placeholders. On-prem
 * tenants ship self-hosted paths so no external network request is
 * made — including for CJK PDFs (cMap data) and PDFs that leave Base14
 * fonts unembedded (standard font data).
 *
 * `GlobalWorkerOptions.workerSrc` is module-scoped; we defer
 * assignment until the first render so the manifest (which is
 * published to the onprem snapshot in `main.tsx`) is guaranteed to be
 * available. The cMap / standard-font URLs flow through `getDocument`
 * options instead of a global, so we resolve them once per document
 * load. */

let workerSrcApplied = false;

interface ResolvedPdfDocOptions {
  readonly cMapUrl?: string;
  readonly cMapPacked?: boolean;
  readonly standardFontDataUrl?: string;
  readonly wasmUrl?: string;
}

function expandVersionPlaceholder(template: string): string {
  return template.replace('{version}', pdfjsLib.version);
}

/**
 * Set `GlobalWorkerOptions.workerSrc` from the tenant manifest.
 * Idempotent; subsequent calls are no-ops once the worker has been
 * applied (or after the tenant has explicitly opted out by clearing
 * the field).
 */
function applyPdfWorkerSrcOnce(): void {
  if (workerSrcApplied) return;
  const tenant = getTenantSnapshot();
  /* No snapshot → tenant hasn't booted. Leave `workerSrc` unset and
   * retry on the next `getDocument()` call. pdf.js will log a
   * `fake worker` warning in the meantime, which is acceptable for
   * the extremely narrow window between module import and
   * `setTenantSnapshot()` in `main.tsx`. */
  if (!tenant) return;
  const template = resolveTenantPdfWorkerUrl(tenant);
  if (!template) {
    /* Tenant explicitly cleared the field (empty string / omitted) —
     * worker stays unset, pdf.js falls back to its fake worker. This
     * is the desired on-prem behaviour when the tenant wants no
     * external network hit and hasn't shipped a self-hosted worker. */
    workerSrcApplied = true;
    return;
  }
  pdfjsLib.GlobalWorkerOptions.workerSrc = expandVersionPlaceholder(template);
  workerSrcApplied = true;
}

/**
 * Resolve the cMap + standard-font URL slots from the tenant manifest.
 * Both fields are optional; absent slots collapse to `undefined`,
 * which pdf.js silently handles by skipping the corresponding lookup
 * (CJK glyphs render as `□`, unembedded Base14 fonts fall back to
 * host fonts). The manifest is the only source of truth — there is
 * deliberately no hardcoded default here so a misconfigured tenant
 * fails closed instead of leaking a CDN egress.
 */
function resolvePdfDocOptions(): ResolvedPdfDocOptions {
  const tenant = getTenantSnapshot();
  if (!tenant) return {};
  const cMapTemplate = resolveTenantPdfCMapUrl(tenant);
  const stdFontTemplate = resolveTenantPdfStandardFontDataUrl(tenant);
  const wasmTemplate = resolveTenantPdfWasmUrl(tenant);
  return {
    cMapUrl: cMapTemplate ? expandVersionPlaceholder(cMapTemplate) : undefined,
    /* `cMapPacked: true` is required because pdfjs-dist ships the
     * binary `.bcmap` variants under `cmaps/`, not the legacy text
     * `.cmap` files. Setting this to `false` would make pdf.js
     * request `*.cmap` files that don't exist. */
    cMapPacked: cMapTemplate ? true : undefined,
    standardFontDataUrl: stdFontTemplate
      ? expandVersionPlaceholder(stdFontTemplate)
      : undefined,
    /* JBIG2 · JPEG2000 디코더. 다른 슬롯과 달리 **없으면 폴백이 없다** —
     * pdfjs-dist 5.x 는 wasm 초기화에 실패하면 예외를 던지고, 스캔 문서의
     * 페이지가 통째로 안 그려진다. */
    wasmUrl: wasmTemplate ? expandVersionPlaceholder(wasmTemplate) : undefined,
  };
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const Viewport = styled.div`
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: auto;
  scrollbar-gutter: stable;
  box-sizing: border-box;
  padding: 20px;
`;

const PagesContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: safe center;
  gap: 16px;
  min-width: 100%;
  border-radius: 0 0 16px 16px;
`;

const PageWrapper = styled.div`
  position: relative;
  line-height: 0;
  flex-shrink: 0;
`;

const PageCanvas = styled.canvas`
  display: block;
  box-sizing: border-box;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 8px;
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.08),
    0 4px 12px rgba(0, 0, 0, 0.06);
  background: #fff;
`;

const PageNumber = styled.div`
  position: absolute;
  bottom: 10px;
  right: 10px;
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.9)'};
  padding: 3px 10px;
  border-radius: 6px;
  pointer-events: none;
  backdrop-filter: blur(4px);
`;

/* ------------------------------------------------------------------ */
/* PdfPage — 단일 페이지 렌더링                                          */
/* ------------------------------------------------------------------ */

const PdfPage: React.FC<{
  pdf: PDFDocumentProxy;
  pageNumber: number;
  scale: number;
  available: PageSize;
  totalPages: number;
}> = ({ pdf, pageNumber, scale, available, totalPages }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const renderTaskRef = useRef<RenderTask | null>(null);

  useEffect(() => {
    let cancelled = false;

    const renderPage = async () => {
      /* 이전 렌더 취소 */
      if (renderTaskRef.current) {
        try { renderTaskRef.current.cancel(); } catch { /* noop */ }
      }

      const page = await pdf.getPage(pageNumber);
      if (cancelled) return;

      const native = page.getViewport({ scale: 1 });
      const fit = fitPageScale(native, available);
      if (fit <= 0) return;
      const viewport = page.getViewport({ scale: fit * scale });
      const canvas = canvasRef.current;
      if (!canvas) return;

      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;

      const renderTask = page.render({
        canvas,
        viewport,
        transform: dpr === 1 ? undefined : [dpr, 0, 0, dpr, 0, 0],
      });
      renderTaskRef.current = renderTask;

      try {
        await renderTask.promise;
      } catch (e: unknown) {
        if ((e as Error).name === 'RenderingCancelledException') return;
        console.error(`[PdfRenderer] Page ${pageNumber} render error:`, e);
      }
    };

    void renderPage().catch((error) => {
      if (!cancelled) console.error(`[PdfRenderer] Page ${pageNumber} render error:`, error);
    });

    return () => {
      cancelled = true;
      if (renderTaskRef.current) {
        try { renderTaskRef.current.cancel(); } catch { /* noop */ }
      }
    };
  }, [pdf, pageNumber, scale, available.width, available.height]);

  return (
    <PageWrapper>
      <PageCanvas ref={canvasRef} />
      <PageNumber>
        {pageNumber} / {totalPages}
      </PageNumber>
    </PageWrapper>
  );
};

/* ------------------------------------------------------------------ */
/* PdfRenderer — 메인 컴포넌트                                          */
/* ------------------------------------------------------------------ */

interface PdfRendererProps {
  file: File;
  /** Zoom relative to a complete page fit. 1 is the default fit-to-page mode. */
  scale: number;
  onLoadComplete?: (numPages: number) => void;
  onLoadStart?: () => void;
  onError?: (error: string) => void;
}

export const PdfRenderer: React.FC<PdfRendererProps> = ({
  file,
  scale,
  onLoadComplete,
  onLoadStart,
  onError,
}) => {
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [numPages, setNumPages] = useState(0);
  const pdfRef = useRef<PDFDocumentProxy | null>(null);
  const { ref: viewportRef, size: available } = useViewportSize();

  useEffect(() => {
    let cancelled = false;

    /* 이전 문서 정리 */
    if (pdfRef.current) {
      pdfRef.current.destroy();
      pdfRef.current = null;
    }
    setPdf(null);
    setNumPages(0);
    onLoadStart?.();

    applyPdfWorkerSrcOnce();
    /* Pull cMap + standard-font URLs at load time (not module init)
     * so a tenant snapshot installed *after* the renderer module is
     * imported is still picked up. Both slots are optional; missing
     * tenant config simply degrades CJK / Base14 rendering rather
     * than failing the load outright. */
    const docOptions = resolvePdfDocOptions();
    // 전달받은 파일을 다시 fetch하면 데스크톱 CSP의 blob: 연결 제한에 걸린다.
    // 바이트를 직접 전달해 웹과 데스크톱에서 같은 로딩 경로를 사용한다.
    const loadDocument = async () => {
      const data = new Uint8Array(await file.arrayBuffer());
      if (cancelled) return null;
      return pdfjsLib.getDocument({ data, ...docOptions }).promise;
    };

    loadDocument()
      .then((doc) => {
        if (!doc) return;
        if (cancelled) {
          doc.destroy();
          return;
        }
        pdfRef.current = doc;
        setPdf(doc);
        setNumPages(doc.numPages);
        onLoadComplete?.(doc.numPages);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('[PdfRenderer] Load error:', err);
        onError?.('PDF 파일을 로드할 수 없습니다.');
      });

    return () => {
      cancelled = true;
      /* ⚠️ loadingTask.destroy() 호출하지 않음
         — pdfjs 공유 Worker를 파괴하여 "Worker was destroyed" 에러 유발.
         cancelled 플래그로 stale 결과를 무시하고,
         로드 완료된 doc은 위 .then()에서 doc.destroy()로 정리. */
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  /* 컴포넌트 최종 언마운트 시 PDF 문서 정리 */
  useEffect(() => {
    return () => {
      if (pdfRef.current) {
        pdfRef.current.destroy();
        pdfRef.current = null;
      }
    };
  }, []);

  return (
    <Viewport ref={viewportRef} data-pdf-viewport>
    <PagesContainer>
      {pdf && Array.from({ length: numPages }, (_, i) => (
        <PdfPage
          key={i + 1}
          pdf={pdf}
          pageNumber={i + 1}
          scale={scale}
          available={available}
          totalPages={numPages}
        />
      ))}
    </PagesContainer>
    </Viewport>
  );
};
